import React, { useCallback, useEffect, useRef } from "react"
import Button from "aldoo-ra/Button"
import { ServePlacement, TrackAdImpression, TrackAdClick } from "aldoo-ra/AldooAds/api"
import { recordTouch } from "aldoo-ra/PlatformAuth/actions/utm-capture"
import Carousel from "./Carousel"
import useAsync from "../../hooks/useAsync"
import useSiteLanguage from "../../hooks/useSiteLanguage"
import useT from "../../hooks/useT"
import { buildContentUrl } from "../../utils/contentRoutes"
import { rememberUTM } from "../../checkout/utm"
import { sizedUrl, srcsetFor } from "aldoo-ra/ImageVariants"

// an ad's pictures as WebP variants of the size they show at (the resizer
// answers ?size=&format=; anything it cannot resize comes back as it is)
const webp = (url, width) => sizedUrl(url, width, "webp")
const webpSet = (url, widths) => srcsetFor(url, widths, "webp") || undefined


// "1920x400" → 400 / 1920, the banner's height per width
const ratioOf = (value, fallback) => {
  const [w, h] = String(value || "").split(/[x×]/).map(Number)
  return w > 0 && h > 0 ? h / w : fallback
}

const hrefOf = (item) => {
  if (item.kind === "product" && item.product?.pid) return buildContentUrl(item.product)
  return item.href || ""
}

/** The link an ad is: a site page through the router, another site in a new tab. */
function AdLink({ item, onClick, className, children, testId = "ad-slide" }) {
  const href = hrefOf(item)
  if (!href) return <div className={className}>{children}</div>
  if (/^https?:\/\//.test(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick} data-testid={testId}>
        {children}
      </a>
    )
  }
  return (
    <Button variant="none" href={href} className={className} onClick={onClick} data-testid={testId}>
      {children}
    </Button>
  )
}

/**
 * One ad of a placement. A banner image is the whole slide (a phone gets
 * its own art when there is one), with its title and subtitle over a shade
 * when it has them; a product with no banner stands on a banner of its own:
 * its cover, its name, author and a button — on the ad's colors when it has
 * them, else over a blur of the cover. Everything is the one link to the
 * ad's destination.
 */
function AdSlide({ item, onClick, height }) {
  const t = useT()
  const cover = item.product?.image || item.product?.thumb || ""
  const style = item.style || {}
  const colored = !!style.background

  const body = item.image ? (
    <div className="relative w-full h-full">
      <picture className="block w-full h-full">
        {item.image_sm && <source media="(max-width: 767px)" srcSet={`${item.image_sm}?size=960&format=webp`} />}
        <source media="(max-width: 960px)" srcSet={`${item.image}?size=1600&format=webp`} />
        <source srcSet={`${item.image}?size=1920&format=webp`} />
        <img src={item.image} alt={item.title || item.product?.name || ""} className="block w-full h-full object-cover object-center" fetchpriority="high" />
      </picture>
      {(item.title || item.subtitle) && (
        <div className="absolute inset-0 flex flex-col items-center justify-end gap-2 md:gap-3 px-4 pb-8 md:pb-10 bg-gradient-to-t from-black/70 to-transparent text-white text-center">
          {item.title && <h2 className="m-0 text-[20px] md:text-[34px] lg:text-[42px] font-bold leading-tight line-clamp-2">{item.title}</h2>}
          {item.subtitle && <p className="m-0 text-[12px] md:text-[16px] line-clamp-1">{item.subtitle}</p>}
        </div>
      )}
    </div>
  ) : (
    <div className="relative w-full h-full overflow-hidden" style={colored ? { backgroundColor: style.background, color: style.text || "#ffffff" } : { backgroundColor: "#1f1f23", color: "#ffffff" }}>
      {!colored && cover && <img src={webp(cover, 320)} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-60" />}
      {!colored && <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/10" />}
      <div className="relative h-full max-w-[1200px] mx-auto flex items-center justify-between gap-6 px-6 md:px-16">
        <div className="min-w-0 flex flex-col gap-2 md:gap-4">
          <h2 className="m-0 text-[20px] md:text-[34px] lg:text-[42px] font-bold leading-tight line-clamp-2">{item.title || item.product?.name}</h2>
          {(item.subtitle || item.product?.author) && <p className="m-0 text-[13px] md:text-[18px] opacity-80 line-clamp-2">{item.subtitle || item.product?.author}</p>}
          <span
            className="self-start mt-1 md:mt-2 inline-flex items-center h-9 md:h-12 px-5 md:px-7 rounded-full text-[13px] md:text-[16px] font-bold"
            style={{ backgroundColor: style.button || "rgb(214,80,54)", color: style.button_text || "#ffffff" }}
          >
            {item.cta || t`See the book`}
          </span>
        </div>
        {cover && (
          <img src={webp(cover, 640)} alt={item.title || item.product?.name || ""} className="shrink-0 h-[80%] max-h-full w-auto rounded-md shadow-2xl object-contain" style={{ maxHeight: `calc(${height} - 32px)` }} />
        )}
      </div>
    </div>
  )

  return (
    <AdLink item={item} onClick={onClick} className="block w-full h-full">
      {body}
    </AdLink>
  )
}

/**
 * A grid placement: its header (title, subtitle and a button, in the
 * placement's colors) beside its products — four, two by two — each an ad
 * of its own that counts its impression and its clicks.
 */
function AdGrid({ header, items, onClick, onSeen, className }) {
  const style = header.style || {}
  useEffect(() => {
    items.forEach((_, i) => onSeen(i))
  }, [items, onSeen])

  const grid = (cls) => (
    <div className={cls}>
      {items.map((item) => {
        const cover = item.image || item.product?.image || item.product?.thumb
        return (
          <AdLink
            key={item.id}
            item={item}
            onClick={onClick(item)}
            className="flex aspect-square items-center justify-center rounded-2xl border border-white/10 bg-white/50 overflow-hidden transition-all duration-500 hover:scale-[1.05] hover:bg-white"
          >
            {cover && <img src={webp(cover, 640)} srcSet={webpSet(cover, [320, 480, 640])} sizes="(max-width: 768px) 45vw, 260px" alt={item.title || ""} className="max-w-full max-h-full object-contain" loading="lazy" />}
          </AdLink>
        )
      })}
    </div>
  )
  const cta = header.cta && header.link && (
    <Button
      variant="primary_l"
      href={header.link}
      className="min-w-[180px] md:min-w-[250px] bg-[color:var(--cta-bg)] hover:bg-[color:color-mix(in_srgb,var(--cta-bg)_80%,transparent)]"
      style={{ "--cta-bg": style.button || "rgb(214,80,54)", color: style.button_text || "#ffffff" }}
    >
      {header.cta}
    </Button>
  )
  return (
    <div className={`w-full flex justify-center ${className}`}>
      <div className="relative flex w-full max-w-[500px] md:max-w-[1100px] lg:max-w-[1200px] overflow-hidden rounded-3xl py-5 md:py-20" style={{ backgroundColor: style.background || "#ffffff", color: style.text || "#202124" }}>
        <div className="hidden md:flex w-1/2 flex-col items-start justify-center gap-6 px-16 lg:px-20 z-[1]">
          {header.title && <h2 className="m-0 text-left font-bold leading-tight text-[30px] md:text-[36px] lg:text-[42px]">{header.title}</h2>}
          {header.subtitle && <p className="m-0 text-left text-[16px] md:text-[20px] leading-normal">{header.subtitle}</p>}
          {cta}
        </div>
        <div className="hidden md:flex w-1/2 items-center justify-center px-4 md:px-9">
          {grid("grid grid-cols-2 gap-3 md:gap-4 w-full max-w-[320px] md:max-w-[420px]")}
        </div>
        <div className="flex md:hidden w-full flex-col items-center px-4 pt-4">
          {grid("grid grid-cols-2 gap-3 w-full max-w-[450px] mb-4")}
          {header.title && <h2 className="m-0 text-center font-bold text-[20px] leading-tight">{header.title}</h2>}
          {header.subtitle && <p className="m-0 mt-2 text-center text-[13px] leading-snug">{header.subtitle}</p>}
          {cta && <div className="pt-3 pb-4">{cta}</div>}
        </div>
      </div>
    </div>
  )
}

/**
 * An Aldoo Ads placement of the site (the home page hero…): its live ads,
 * in their order, as a carousel, one banner or a grid with a header. Each ad shown counts
 * an impression once; a click counts a click and hands the ad's utm codes
 * to the checkout and the sign-up attribution, so the ad's revenue shows up
 * in its KPIs. Nothing live → `fallback`.
 */
export default function AdPlacement({ placement: key, fallback = null, className = "", initial = null }) {
  const language = useSiteLanguage()
  // `initial`: the placement as the server served it (the home hero), shown at once
  const { result, pending } = useAsync(() => ServePlacement(key, language), [key, language], { initial })
  const seen = useRef(new Set())

  const items = result?.result ? result.items || [] : []
  const settings = result?.placement?.settings || {}
  const format = result?.placement?.format || "carousel"
  const ratio = ratioOf(settings.ratio, 400 / 1920)
  // The art's height on a wide screen. A narrow one crops its sides, and the
  // part that matters is the middle (`safe_area`, a share of the width — the
  // middle half by default): the slot grows toward min_height, but never so
  // tall that the art, scaled to it, is wider than the screen can show of
  // that middle.
  const safe = Math.min(1, Math.max(0.2, Number(settings.safe_area) || 0.5))
  const wide = `${(ratio * 100).toFixed(3)}vw`
  const safeCap = `${((ratio / safe) * 100).toFixed(3)}vw`
  const height = `min(${settings.max_height || 520}px, max(${wide}, min(${settings.min_height || 200}px, ${safeCap})))`

  const impression = useCallback(
    (index) => {
      const item = items[index]
      if (!item || seen.current.has(item.id)) return
      seen.current.add(item.id)
      TrackAdImpression(item.id)
    },
    [items]
  )

  const click = (item) => () => {
    TrackAdClick(item.id)
    const utm = {
      utm_source: typeof window === "undefined" ? "" : window.location.host,
      utm_medium: result?.utm_medium || "aldoo-ads",
      utm_campaign: item.campaign,
      utm_content: item.id,
    }
    rememberUTM(utm)
    recordTouch(utm)
  }

  if (!result && pending) return format === "grid" ? null : <div className={`w-full ${className}`} style={{ height }} aria-hidden />
  if (!items.length) return fallback

  if (format === "grid") {
    return (
      <div data-testid={`ad-placement-${key}`} className="w-full">
        <AdGrid header={result.placement.header || {}} items={items} onClick={click} onSeen={impression} className={`px-4 md:px-[70px] lg:px-[110px] xl:px-[140px] ${className}`} />
      </div>
    )
  }

  if (format === "banner" || items.length === 1) {
    return (
      <div className={`w-full ${className}`} style={{ height }} data-testid={`ad-placement-${key}`} ref={() => impression(0)}>
        <AdSlide item={items[0]} onClick={click(items[0])} height={height} />
      </div>
    )
  }

  return (
    <div className={`w-full ${className}`} data-testid={`ad-placement-${key}`}>
      <Carousel autoplay={settings.autoplay !== false} interval={Number(settings.interval) || 5000} duration={settings.duration || 800} height={height} animation={settings.animation || "slide"} onIndexChange={impression}>
        {items.map((item) => (
          <AdSlide key={item.id} item={item} onClick={click(item)} height={height} />
        ))}
      </Carousel>
    </div>
  )
}
